// Desplazamiento a las secciones del inicio (catálogo, testimonios, preguntas...).
//
// Es el ÚNICO punto de la app que decide CÓMO se baja a una sección.
//
// Historial del bug: los botones del checkout "no hacían nada" o aterrizaban
// siempre arriba del inicio. Causas encontradas, una por una:
//   1) Doble invocación (enlace + gestor global) → dos saltos peleándose.
//   2) Un "escape de usuario" (wheel/touchstart/keydown) que se disparaba con
//      eventos residuales del clic y cancelaba el salto antes de aterrizar.
//   3) El destino viajaba SOLO por el `state` de React Router. Si el navegador
//      recargaba la página al navegar, ese `state` se perdía → volvíamos arriba.
//   4) Las secciones de la home (FAQ, testimonios, productos) montan de forma
//      ASÍNCRONA. Cuando aparecen, empujan el destino hacia abajo. Si el salto
//      ya había terminado de "reafirmarse", quedábamos por encima del destino.
//
// Este motor es DETERMINISTA y cubre todo lo anterior:
//   - El destino se guarda además en sessionStorage (sobrevive a recargas).
//   - Espera a que la sección exista en el DOM (poll corto).
//   - Salta de forma INSTANTÁNEA (behavior: 'auto'), que no se puede interrumpir.
//   - REAFIRMA la posición por temporizadores Y observando el tamaño del
//     documento (ResizeObserver): si algo empuja el layout, se re-ajusta.
//   - NO tiene escape de usuario que lo mate. Un nuevo salto cancela al anterior
//     mediante el token global + limpieza de recursos.

// Alto del navbar fijo de la tienda (h-16 = 64px).
const NAV_OFFSET = 64;

// Clave donde se guarda la intención de navegación para que sobreviva a una
// recarga completa del navegador (cuando el `state` de React Router no llega).
const PENDING_KEY = 'pcp.pendingSection';

// Token global: cada nueva solicitud invalida la anterior. Evita que dos saltos
// se peleen por la misma sección.
let activeToken = 0;
// Limpieza de la solicitud en curso (temporizadores y observers). Se ejecuta al
// empezar otra solicitud para no dejar recursos colgando.
let activeCleanup: (() => void) | null = null;

// Guarda la sección destino antes de navegar. Si el navegador recarga, el
// gestor global la recupera con consumePendingSection().
export function requestSectionNav(section: string): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, section);
  } catch {
    // sessionStorage no disponible: seguimos con el `state` de React Router.
  }
}

// Lee y limpia la sección destino guardada. Devuelve '' si no hay ninguna.
export function consumePendingSection(): string {
  try {
    const value = window.sessionStorage.getItem(PENDING_KEY);
    if (value) window.sessionStorage.removeItem(PENDING_KEY);
    return value ?? '';
  } catch {
    return '';
  }
}

export function scrollToSection(section: string): void {
  const token = ++activeToken;
  if (activeCleanup) {
    activeCleanup();
    activeCleanup = null;
  }
  const isActive = () => token === activeToken;

  const jump = (): boolean => {
    const el = document.getElementById(section);
    if (!el) return false;
    const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET);
    // 'auto' = instantáneo. No se puede interrumpir ni queda a medias.
    window.scrollTo({ top, behavior: 'auto' });
    return true;
  };

  const timers: number[] = [];
  let observer: ResizeObserver | null = null;
  let windowBound = false;

  const onViewportChange = () => {
    if (isActive()) jump();
  };

  const cleanup = () => {
    timers.forEach((t) => window.clearTimeout(t));
    timers.length = 0;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (windowBound) {
      window.removeEventListener('resize', onViewportChange);
      windowBound = false;
    }
  };
  activeCleanup = cleanup;

  // Reafirma la posición mientras el layout puede seguir creciendo (secciones
  // que montan tarde). Como el salto es instantáneo, cada reafirmación
  // reposiciona sin animación que se pueda cortar.
  const reaffirm = () => {
    [80, 200, 400, 700, 1100, 1600, 2200, 2900, 3600].forEach((delay) => {
      timers.push(window.setTimeout(() => {
        if (isActive()) jump();
      }, delay));
    });
    // Re-afirma ante CUALQUIER cambio de tamaño del documento (contenido que
    // aparece y empuja el destino). Así no aterrizamos lejos si algo carga tarde.
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (isActive()) jump();
      });
      observer.observe(document.documentElement);
    }
    window.addEventListener('resize', onViewportChange);
    windowBound = true;

    // La ventana de reafirmación se cierra sola: evita observers eternos.
    timers.push(window.setTimeout(() => {
      if (isActive()) cleanup();
    }, 4200));
  };

  let attempts = 0;
  const tryJump = () => {
    if (!isActive()) return;
    if (jump()) {
      reaffirm();
      return;
    }
    // La sección aún no existe (la home todavía monta sus secciones). Reintenta.
    if (attempts < 80) {
      attempts += 1;
      timers.push(window.setTimeout(tryJump, 60));
      return;
    }
    // Si de plano no existe (p. ej. testimonios desactivados), no dejamos al
    // usuario colgado: subimos al inicio.
    if (isActive()) window.scrollTo({ top: 0, behavior: 'auto' });
  };

  tryJump();
}